import Markdown from 'markdown-to-jsx';
import React, { useRef, useState } from 'react';

import { SettingsListGroup, SettingsListRow } from '@eluve/blocks';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Input,
  Kbd,
  Separator,
  Text,
  useToast,
} from '@eluve/components';
import { Snippet, SnippetModal } from '@eluve/eluve-editor';
import { useSnippets } from '@eluve/frontend-appointment-hooks';
import { useTranslation } from '@eluve/i18n';
import { useDialog } from '@eluve/utility-hooks';
import { getVendorDataMapper } from '@eluve/vendor';

export const TenantUserSnippetsList = () => {
  const { t } = useTranslation();
  const { snippets, loading, addSnippet, updateSnippet, deleteSnippet } =
    useSnippets();
  const toast = useToast();

  const { isDialogOpen, setIsDialogOpen } = useDialog();
  const [editSnippetId, setEditSnippetId] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resetModal = () => {
    setIsDialogOpen(false);
    setEditSnippetId(undefined);
  };

  const onAddNewClick = () => {
    setIsDialogOpen(true);
    setEditSnippetId(undefined);
  };

  const saveSnippet = async (title: string, content: string) => {
    resetModal();
    if (editSnippetId) {
      await updateSnippet(editSnippetId, title, content);
    } else {
      await addSnippet(title, content);
    }
  };

  const editSnippet = (snippetId: string) => {
    setIsDialogOpen(true);
    const snippet = snippets.find((s) => s.id === snippetId);
    if (!snippetId || !snippet) {
      return;
    }
    setEditSnippetId(snippetId);
  };

  const handleExport = () => {
    const snippetJson = JSON.stringify(
      snippets.map((s) => ({ title: s.title, content: s.content })),
      null,
      2,
    );
    const blob = new Blob([snippetJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'eluve-snippets.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setIsImporting(true);
      const text = await file.text();
      const jsonData = JSON.parse(text);
      let importedSnippets: Snippet[] = [];

      if (Array.isArray(jsonData)) {
        importedSnippets = jsonData;
      } else if (jsonData?.snippets && Array.isArray(jsonData?.snippets)) {
        importedSnippets = jsonData.snippets;
      } else if (typeof jsonData === 'object' && jsonData !== null) {
        const vendorProvider = getVendorDataMapper('JANE');
        if (vendorProvider.convertPhrasesToSnippets) {
          importedSnippets = vendorProvider.convertPhrasesToSnippets(jsonData);
        }
      } else {
        throw new Error('Unknown file format');
      }

      if (importedSnippets?.length > 0) {
        await Promise.all(
          importedSnippets.map((snippet) =>
            addSnippet(snippet.title, snippet.content),
          ),
        );
        toast.success(
          t('home.snippets.importSuccess', { count: importedSnippets.length }),
        );
      }
    } catch {
      toast.error(t('home.snippets.importParseError'));
    } finally {
      e.target.value = '';
      setIsImporting(false);
    }
  };

  return (
    <>
      <SettingsListGroup
        title={t('home.snippets.title')}
        description={
          <>
            {t('home.snippets.descriptionPrefix')} <Kbd>/</Kbd>{' '}
            {t('home.snippets.descriptionSuffix')}
          </>
        }
        countLabel={
          snippets.length === 1
            ? t('home.snippets.countLabelOne', { count: snippets.length })
            : t('home.snippets.countLabelMany', { count: snippets.length })
        }
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  loading={isImporting}
                  aria-label={t('home.snippets.actionsAriaLabel')}
                >
                  <Icon name="EllipsisVertical" size="auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handleExport}>
                  <Icon name="Upload" size="lg" />
                  <div>{t('home.snippets.export')}</div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleImport}>
                  <Icon name="Import" size="lg" />
                  <div>{t('home.snippets.import')}</div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={onAddNewClick}>
              {t('home.snippets.createNew')}
            </Button>
          </>
        }
      >
        {loading ? (
          <Text size="sm" color="tertiary" className="px-6 py-7">
            {t('home.snippets.loading')}
          </Text>
        ) : snippets.length === 0 ? (
          <Text size="sm" color="tertiary" className="px-6 py-7">
            {t('home.snippets.noSnippets')}
          </Text>
        ) : (
          snippets.map((snippet, i) => (
            <div key={String(snippet.id)}>
              <SettingsListRow
                editLabel={t('home.snippets.editSnippet', {
                  title: snippet.title,
                })}
                deleteLabel={t('home.snippets.deleteSnippet', {
                  title: snippet.title,
                })}
                onEdit={() => editSnippet(String(snippet.id))}
                onDelete={() => deleteSnippet(String(snippet.id))}
                disabled={String(snippet.id).startsWith('temp-')}
              >
                <Kbd>/{snippet.title}</Kbd>
                <div className="max-w-3xl text-xs font-medium wrap-break-word text-text-tertiary">
                  <Markdown>{snippet.content ?? ''}</Markdown>
                </div>
              </SettingsListRow>
              {i < snippets.length - 1 && <Separator className="my-0" />}
            </div>
          ))
        )}
      </SettingsListGroup>

      <Input
        type="file"
        accept=".json"
        ref={fileInputRef}
        onChange={onFileSelected}
        className="hidden"
      />

      <SnippetModal
        snippet={
          editSnippetId
            ? snippets.find((s) => s.id === editSnippetId)
            : undefined
        }
        isOpen={isDialogOpen}
        onOpenChange={resetModal}
        onClose={resetModal}
        onSave={saveSnippet}
      />
    </>
  );
};
